import { KeyValueStore } from './KeyValueStore';
import { Project, SelectionLike } from './types';

export const DEFAULT_PROJECT = 'default';

/**
 * Persists projects into an injected key-value store (one entry per project, keyed by name).
 * The store and the region are bound by the app at init; the class itself is pure logic.
 */
export class ProjectsRepository<TSelection extends SelectionLike = SelectionLike> {
    private readonly store: KeyValueStore;

    private readonly region: string;

    private readonly defaultProjectPrefix: string;

    public constructor(store: KeyValueStore, region: string, defaultProjectPrefix: string = DEFAULT_PROJECT) {
        this.store = store;
        this.region = region;
        this.defaultProjectPrefix = defaultProjectPrefix;
    }

    public getDefaultProjectName(): string {
        return `${this.defaultProjectPrefix}_${this.region}`;
    }

    public async init(): Promise<Project<TSelection>> {
        const defaultProject = await this.store.getItem<Project<TSelection>>(this.getDefaultProjectName());
        if (defaultProject === null || (defaultProject && defaultProject.createdAt === undefined)) {
            return this.create(this.getDefaultProjectName());
        }
        return defaultProject;
    }

    public async count(): Promise<number> {
        return this.store.length();
    }

    public async list(limit: number, offset: number): Promise<Project<TSelection>[]> {
        const keys = await this.store.keys();
        const results = await Promise.all(keys
            .slice(offset, offset + limit)
            .map((key) => this.store.getItem<Project<TSelection>>(key)));
        return results.filter((v) => v !== null) as Project<TSelection>[];
    }

    public async create(name: string): Promise<Project<TSelection>> {
        const check = await this.store.getItem(name);
        if (check !== null) {
            throw new Error(`project with name "${name}" already exist`);
        }
        const project: Project<TSelection> = {
            name,
            selections: [],
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        await this.store.setItem(name, project);
        return project;
    }

    public async get(name: string): Promise<Project<TSelection>> {
        const project = await this.store.getItem<Project<TSelection>>(name);
        if (project === null) {
            throw new Error(`project with name "${name}" not found`);
        }
        return project;
    }

    public async update(project: Project<TSelection>): Promise<boolean> {
        await this.store.setItem(project.name, project);
        return true;
    }
}
